import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Search,
  FilterList,
  Visibility,
  Refresh,
  TrendingUp,
  TrendingDown,
  AccountBalanceWallet,
  Payment,
  EmojiEvents,
  SwapHoriz,
  ExpandMore,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  fetchTransactions,
  setFilters,
  clearFilters,
  setPage,
} from '../store/slices/transactionsSlice';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { Transaction } from '../types';

const TransactionsPage: React.FC = () => {
  const dispatch = useDispatch();
  const { transactions, pagination, statistics, loading, error } = useSelector(
    (state: RootState) => state.transactions
  );
  
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);

  useEffect(() => {
    dispatch(fetchTransactions({ page: 1, limit: 10 }) as any);
  }, [dispatch]);

  const handleSearch = () => {
    const filters: any = {};
    if (searchTerm) {
      // Check if search term looks like a phone number
      if (searchTerm.startsWith('251')) {
        filters.phoneNumber = searchTerm;
      } else {
        filters.username = searchTerm;
      }
    }
    if (typeFilter) filters.type = typeFilter;
    
    dispatch(setFilters(filters));
    dispatch(fetchTransactions({ page: 1, limit: 10, filters }) as any);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(fetchTransactions({ page: newPage + 1, limit: 10 }) as any);
  };

  const handleViewTransaction = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleClearFilters = () => {
    dispatch(clearFilters());
    dispatch(fetchTransactions({ page: 1, limit: 10 }) as any);
    setSearchTerm('');
    setTypeFilter('');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'deposit': return 'success';
      case 'withdraw': return 'warning';
      case 'win':
      case 'tournament_win':
      case 'ludo_win': return 'info';
      case 'purchase':
      case 'tournament_purchase':
      case 'ludo_purchase': return 'primary';
      case 'house_fee':
      case 'ludo_house_fee': return 'error';
      case 'refund':
      case 'tournament_refund':
      case 'ludo_refund': return 'default';
      case 'transfer': return 'secondary';
      case 'bonus': return 'success';
      default: return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'deposit': return <TrendingUp />;
      case 'withdraw': return <TrendingDown />;
      case 'win':
      case 'tournament_win':
      case 'ludo_win': return <EmojiEvents />;
      case 'purchase':
      case 'tournament_purchase':
      case 'ludo_purchase': return <Payment />;
      case 'transfer': return <SwapHoriz />;
      default: return <AccountBalanceWallet />;
    }
  };

  const transactionTypes = [
    { value: 'deposit', label: 'Deposit' },
    { value: 'withdraw', label: 'Withdraw' },
    { value: 'win', label: 'Win' },
    { value: 'tournament_win', label: 'Tournament Win' },
    { value: 'ludo_win', label: 'Ludo Win' },
    { value: 'purchase', label: 'Purchase' },
    { value: 'tournament_purchase', label: 'Tournament Purchase' },
    { value: 'tournament_refund', label: 'Tournament Refund' },
    { value: 'refund', label: 'Refund' },
    { value: 'house_fee', label: 'House Fee' },
    { value: 'bonus', label: 'Bonus' },
    { value: 'transfer', label: 'Transfer' },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.5rem", sm: "2rem" }, mb: { xs: 1, sm: 2 } }}>
        Transactions Management
      </Typography>

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 } }}>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AccountBalanceWallet sx={{ mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.totalTransactions}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Transactions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp sx={{ mr: 2, color: 'success.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.deposits}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Deposits
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingDown sx={{ mr: 2, color: 'warning.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.withdrawals}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Withdrawals
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <EmojiEvents sx={{ mr: 2, color: 'info.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.wins}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Wins
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <SwapHoriz sx={{ mr: 2, color: 'secondary.main' }} />
                <Box>
                  <Typography variant="h6">{statistics.transfers}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Transfers
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 160px', minWidth: 140 }}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Payment sx={{ mr: 2, color: 'error.main' }} />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(statistics.totalAmount)}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Amount
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, flex: 1 }}>
            <TextField
              fullWidth
              placeholder="Search by username or phone number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <FormControl fullWidth sx={{ minWidth: { xs: '100%', sm: 200 } }} size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
              >
                <MenuItem value="">All Types</MenuItem>
                {transactionTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 1, 
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            {/* First row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Clear
              </Button>
            </Box>
            
            {/* Second row of buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexDirection: { xs: 'row', sm: 'row' },
              width: { xs: '100%', sm: 'auto' }
            }}>
              <Button
                variant="outlined"
                onClick={() => setShowFilters(!showFilters)}
                startIcon={<FilterList />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                More Filters
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Transactions Table */}
      <Paper>
        <TableContainer sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <Table sx={{ minWidth: { xs: 650, md: 850 } }}>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Balance After</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.filter((transaction: Transaction) => transaction.user !== null).map((transaction: Transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {transaction.user?.username || 'Unknown User'}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {transaction.user?.phoneNumber || 'No Phone'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getTypeIcon(transaction.type)}
                      label={transaction.type}
                      color={getTypeColor(transaction.type) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                      fontWeight="bold"
                    >
                      {transaction.amount >= 0 ? '+' : ''}{formatCurrency(transaction.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold">
                      {formatCurrency(transaction.balanceAfter)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {transaction.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {dayjs(transaction.createdAt).format('MMM DD, YYYY HH:mm')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewTransaction(transaction)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 20, 50]}
          sx={{
            '& .MuiTablePagination-toolbar': {
              flexDirection: 'row',
              gap: { xs: 0.5, sm: 1 },
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
            },
            '& .MuiTablePagination-spacer': {
              display: 'none',
            },
            '& .MuiTablePagination-selectLabel': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 2, sm: 0 },
            },
            '& .MuiTablePagination-select': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              order: { xs: 1, sm: 0 },
            },
            '& .MuiTablePagination-actions': {
              order: { xs: 3, sm: 0 },
            },
          }}
        />
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* Transaction Details Modal */}
      <Dialog open={showTransactionDetails} onClose={() => setShowTransactionDetails(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Transaction Details</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTransaction && (
            <Box>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Basic Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Transaction ID</Typography>
                      <Typography variant="body1">{selectedTransaction.id}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Type</Typography>
                      <Chip
                        icon={getTypeIcon(selectedTransaction.type)}
                        label={selectedTransaction.type}
                        color={getTypeColor(selectedTransaction.type) as any}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Amount</Typography>
                      <Typography
                        variant="body1"
                        color={selectedTransaction.amount >= 0 ? 'success.main' : 'error.main'}
                        fontWeight="bold"
                      >
                        {selectedTransaction.amount >= 0 ? '+' : ''}{formatCurrency(selectedTransaction.amount)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Balance After</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {formatCurrency(selectedTransaction.balanceAfter)}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">User Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Username</Typography>
                      <Typography variant="body1">{selectedTransaction.user?.username || 'Unknown User'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Phone Number</Typography>
                      <Typography variant="body1">{selectedTransaction.user?.phoneNumber || 'No Phone'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">User ID</Typography>
                      <Typography variant="body1">{selectedTransaction.user?.id || 'Unknown ID'}</Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {(selectedTransaction.recipient || selectedTransaction.senderUsername) && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="h6">Transfer Information</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      {selectedTransaction.recipient && (
                        <>
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary">Recipient</Typography>
                            <Typography variant="body1">{selectedTransaction.recipientUsername}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary">Recipient Telegram ID</Typography>
                            <Typography variant="body1">{selectedTransaction.recipientTelegramId || 'N/A'}</Typography>
                          </Box>
                        </>
                      )}
                      {selectedTransaction.senderUsername && (
                        <>
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary">Sender</Typography>
                            <Typography variant="body1">{selectedTransaction.senderUsername}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2" color="textSecondary">Sender Telegram ID</Typography>
                            <Typography variant="body1">{selectedTransaction.senderTelegramId || 'N/A'}</Typography>
                          </Box>
                        </>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="h6">Description & Timestamps</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                      <Typography variant="body1">{selectedTransaction.description}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                      <Typography variant="body1">
                        {dayjs(selectedTransaction.createdAt).format('MMM DD, YYYY HH:mm:ss')}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Last Updated</Typography>
                      <Typography variant="body1">
                        {dayjs(selectedTransaction.lastUpdated).format('MMM DD, YYYY HH:mm:ss')}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowTransactionDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TransactionsPage; 